import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import Modal from 'components/ui/Modal';
import RadioFieldSet from 'components/ui/RadioFieldSet/RadioFieldSet';
import { CancelButton, ConfirmButton } from 'components/ui/Button';
import { legsShape } from 'constants/propTypes';

import { ButtonWrapper, FieldContainer } from './PrrThroughFareConfirmation.styles';
import { isNewPriceLessExpensive } from 'utils/ticket';

const PrrThroughFareConfirmation = ({ isOpen, onClose, onContinue, selectedItemRefs, selectedLegs }) => {
  const [selectedItem, setSelectedItem] = useState(null);

  const journeyDirection = selectedLegs[0]?.direction;
  const passengers = new Set();

  let individualDisabled = false;

  const groupedLegs = selectedLegs.map((leg) => ({
    segment: `${leg.serviceType.code} ${leg.trainNumber}`,
    disabled: isNewPriceLessExpensive(leg),
    selectedRefs: leg.products
      .map(({ itemRef, passengerId }) => {
        passengers.add(passengerId);

        return selectedItemRefs.includes(itemRef) ? itemRef : null;
      })
      .filter(Boolean)
  }));

  individualDisabled = groupedLegs.some(({ disabled }) => disabled);

  groupedLegs.forEach((leg) => {
    leg.disabled = individualDisabled;
  });

  const combinedLegs = groupedLegs.flatMap((v, i) =>
    groupedLegs.slice(i + 1).map((w) => ({
      segment: v.segment + ' & ' + w.segment,
      selectedRefs: v.selectedRefs.concat(w.selectedRefs)
    }))
  );

  const legsOptions = useMemo(() => [...groupedLegs, ...combinedLegs], [combinedLegs, groupedLegs]);

  const handleSelectFare = (event) => setSelectedItem(event.target.value);
  const handleContinue = () => onContinue(legsOptions.find(({ segment }) => segment === selectedItem).selectedRefs);
  const handleClose = () => {
    setSelectedItem(null);
    passengers.clear();
    onClose();
  };

  useEffect(() => {
    if (!selectedItem && individualDisabled && combinedLegs.length > 0) {
      setSelectedItem(combinedLegs[0].segment);
    } else if (!selectedItem && groupedLegs.length > 0) {
      const defaultLeg =
        groupedLegs.find(({ segment }) => segment.includes('ES'))?.segment ||
        groupedLegs.find(({ segment }) => segment.includes('ER'))?.segment;

      setSelectedItem(defaultLeg);
    }
  }, [selectedItem, groupedLegs, combinedLegs, individualDisabled]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={<FormattedMessage id="prrRefund.throughFareConfirmationTitle" />}
      subtitle={
        <>
          <div>
            <FormattedMessage id="prrRefund.delayWaningSubtitleBis" />
          </div>
          <FormattedMessage id="prrRefund.throughFareConfirmationSubTitle" />
        </>
      }
    >
      <FieldContainer>
        <RadioFieldSet
          noDefault
          name="through-fare-selection"
          radioButtons={legsOptions.map(({ segment, disabled }) => ({ label: segment, value: segment, disabled }))}
          selectedValue={selectedItem}
          onChange={handleSelectFare}
          legend={
            <>
              <FormattedMessage id={`journeySummary.${journeyDirection}`} />:{' '}
              <span style={{ textTransform: 'lowercase', fontWeight: 400 }}>
                {passengers.size} <FormattedMessage id="bookingMagnet.passengers" />
              </span>
            </>
          }
        />
      </FieldContainer>
      <ButtonWrapper>
        <ConfirmButton
          data-testid="prr-refund-through-fare-continue"
          data-cy="prr-refund-through-fare-continue"
          onClick={handleContinue}
          disabled={!selectedItem}
        >
          <FormattedMessage id="exchanges.continue" />
        </ConfirmButton>
        <CancelButton
          type="button"
          data-cy="prr-refund-through-fare-cancel"
          data-testid="prr-refund-through-fare-cancel"
          onClick={handleClose}
        >
          <FormattedMessage id="vouchers.cancel" />
        </CancelButton>
      </ButtonWrapper>
    </Modal>
  );
};

PrrThroughFareConfirmation.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onContinue: PropTypes.func,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  selectedLegs: legsShape
};

export default PrrThroughFareConfirmation;
